import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {media, FONT_SIZE} from '../../components/ui/theme';
import { ModalShell } from '../../components/ui/ModalShell';
import {
  ModalHeader,
  ModalHeaderTitle,
  ModalCloseBtn,
  ModalBody,
  ModalFooter,
  ButtonPrimary,
  ButtonSecondary,
  INK,
} from '../../components/ui/ModalDA';

/**
 * EpicRegisterModal — Modal "Have you already created an Earniverse account on Epic Games Store?"
 * pour les utilisateurs qui se connectent via Epic SSO.
 *
 * 3 steps :
 *   - step 0 : "Have you already created an Earniverse account?" → Yes / No
 *   - step 1 : "Click below to link your account" → Link my account
 *   - step 2 : "Start enjoying Earniverse by linking your account" → Create / Do it later
 *
 * Migration P3 (2026-06-06) : MUI Dialog + Button + Typography + HighlightOffIcon + useStyles
 * → ModalShell + primitives DA. A11y gratuite via ModalShell (ESC + scrollLock + focusTrap).
 *
 * @see tasks/active/TRACK-2026-06-06-da-middleware-p3-batch2.md
 */

const Question = styled.p`
  margin: 0 0 1.5rem;
  font-size: ${FONT_SIZE.base};
  line-height: 1.5;
  color: ${INK};
  text-align: center;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  flex-wrap: wrap;
  ${media.sm`flex-direction: column; align-items: stretch;`}
`;

const ButtonsCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: stretch;
  width: 100%;
`;

function EpicRegisterModal({ open, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);

  const EPIC_CLIENT_ID = process.env.REACT_APP_EPIC_CLIENT_ID;
  const EPIC_REDIRECT_URI = `${process.env.REACT_APP_URL}/epic-sso`;

  const epicSignin = () => {
    localStorage.removeItem('_first');
    const authUrl = `https://www.epicgames.com/id/authorize?client_id=${EPIC_CLIENT_ID}&response_type=code&redirect_uri=${EPIC_REDIRECT_URI}&scope=basic_profile%20friends_list%20presence%20country&prompt=consent`;
    window.location.href = authUrl;
  };

  const titleByStep = [t('EpicTitleAccount'), t('EpicTitleLinkAccount'), t('EpicTitleCreateAccount')];

  return (
    <ModalShell isOpen={open} onClose={onClose} $width="34rem" aria-label={t('EpicGamesAccountLinking')}>
      <ModalHeader>
        <ModalHeaderTitle>{titleByStep[step] || 'Epic Games'}</ModalHeaderTitle>
        <ModalCloseBtn onClick={onClose} aria-label={t('Close')}>✕</ModalCloseBtn>
      </ModalHeader>
      <ModalBody>
        {step === 0 && (
          <>
            <Question>{t('EpicAlreadyCreatedQuestion')}</Question>
            <ButtonsRow>
              <ButtonPrimary onClick={() => setStep(1)}>{t('Yes')}</ButtonPrimary>
              <ButtonSecondary onClick={() => setStep(2)}>{t('No')}</ButtonSecondary>
            </ButtonsRow>
          </>
        )}

        {step === 1 && (
          <>
            <Question>{t('EpicLinkAccountQuestion')}</Question>
            <ButtonsRow>
              <ButtonPrimary onClick={epicSignin}>{t('EpicLinkMyAccount')}</ButtonPrimary>
            </ButtonsRow>
          </>
        )}

        {step === 2 && (
          <>
            <Question>{t('EpicStartEnjoyingQuestion')}</Question>
            <ButtonsCol>
              <ButtonPrimary onClick={epicSignin}>{t('EpicCreateMyAccount')}</ButtonPrimary>
              <ButtonSecondary onClick={onClose}>{t('EpicDoItLater')}</ButtonSecondary>
            </ButtonsCol>
          </>
        )}
      </ModalBody>
    </ModalShell>
  );
}

export default EpicRegisterModal;
